import { CodeHighlightNode, CodeNode } from "@lexical/code";
import { AutoLinkNode, LinkNode } from "@lexical/link";
import { ListItemNode, ListNode } from "@lexical/list";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import { TableCellNode, TableNode, TableRowNode } from "@lexical/table";

import { $generateNodesFromDOM } from "@lexical/html";
import {
  $createParagraphNode,
  $getRoot,
  $isDecoratorNode,
  $isElementNode,
} from "lexical";
import { useId, useMemo } from "react";
import { ImageNode } from "./nodes/ImageNode";
import { LinkClickPlugin } from "./plugins/LinkClickPlugin";
import UpdateContentPlugin from "./plugins/UpdateContentPlugin";
import "./styles.css";
import Theme from "./Theme";

function createEditorStateFromHTML(html) {
  return (editor) => {
    const parser = new DOMParser();
    const dom = parser.parseFromString(html, "text/html");

    const nodes = $generateNodesFromDOM(editor, dom);

    const root = $getRoot();
    root.clear();

    const wrappedNodes = nodes.map((node) => {
      if ($isElementNode(node) || $isDecoratorNode(node)) {
        return node;
      }
      const paragraph = $createParagraphNode();
      paragraph.append(node);
      return paragraph;
    });

    root.append(...wrappedNodes);
  };
}

/**
 * RichTextViewer — A lightweight, display-only Lexical renderer.
 *
 * Use this instead of RichTextEditor when you only need to display rich text
 * content without any editing capabilities. It omits the toolbar, history,
 * autofocus, change tracking, and all editing plugins, resulting in a simpler
 * component tree and smaller runtime footprint.
 *
 * Props:
 * @param {string}          [id] — HTML id used to associate the label with the content via aria-labelledby.
 * @param {React.ReactNode} [label]                    — Visible label rendered above the content. When provided, the content uses aria-labelledby for accessibility.
 * @param {string}          [ariaLabel]                — Screen-reader-only label. Used as aria-label when no visible label is provided. At least one of `label` or `ariaLabel` is required.
 * @param {string}          [value]             — HTML content to display. Supports both HTML strings and plain text. Updates are reflected via UpdateContentPlugin.
 */
const RichTextViewer = ({ id, label, ariaLabel, value }) => {
  if (process.env.NODE_ENV !== "production" && !label && !ariaLabel) {
    console.error(
      "RichTextViewer: either `label` or `ariaLabel` is required for accessibility."
    );
  }

  const generatedId = useId();
  const labelId = id || generatedId;

  const initialConfig = useMemo(
    () => ({
      editable: false,
      editorState: value ? createEditorStateFromHTML(value) : undefined,
      theme: Theme,
      onError(error) {
        throw error;
      },
      nodes: [
        HeadingNode,
        ListNode,
        ListItemNode,
        QuoteNode,
        CodeNode,
        CodeHighlightNode,
        TableNode,
        TableCellNode,
        TableRowNode,
        AutoLinkNode,
        LinkNode,
        ImageNode,
      ],
    }),
    []
  );

  return (
    <>
      {label && <span id={labelId}>{label}</span>}
      <LexicalComposer initialConfig={initialConfig}>
        <div
          style={{ marginTop: label ? "0.75rem" : 0 }}
          className="editor-container read-only"
        >
          <div className="editor-inner">
            <RichTextPlugin
              contentEditable={
                <ContentEditable
                  {...(label
                    ? { "aria-labelledby": labelId }
                    : { "aria-label": ariaLabel })}
                  className="editor-input read-only"
                />
              }
              ErrorBoundary={LexicalErrorBoundary}
            />
            <UpdateContentPlugin html={value} />
            <LinkClickPlugin />
          </div>
        </div>
      </LexicalComposer>
    </>
  );
};

export default RichTextViewer;
